import React, { memo, useEffect, lazy, Suspense } from "react";
import { Home } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { UseFormReturn } from "react-hook-form";
import { ListingFormValues } from "@/components/listings/filters/FilterSchema";
import { Skeleton } from "@/components/ui/skeleton";
import { FormDescription } from "@/components/ui/form-description";
import { useListingFormContext } from "@/context/ListingFormContext";

// Lazy load sections that aren't immediately visible
const BasicDetailsSection = lazy(
  () => import("./sections/BasicDetailsSection")
);
const PricingDetailsSection = lazy(
  () => import("./sections/PricingDetailsSection")
);

interface PropertyInformationStepProps {
  form: UseFormReturn<ListingFormValues>;
}

// Memoize the form description to prevent unnecessary re-renders
const FormDescriptionMemo = memo(() => (
  <FormDescription className="mb-4 p-3 bg-propertylk-lightblue rounded-md text-propertylk-darkblue">
    Start by selecting the property type and listing type, then enter a price.
    These details help potential buyers/renters find your property quickly.
  </FormDescription>
));

FormDescriptionMemo.displayName = "FormDescriptionMemo";

const SectionSkeleton = () => (
  <div className="space-y-4">
    <Skeleton className="h-6 w-1/4" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
  </div>
);

// Memoize the component to prevent unnecessary re-renders
const PropertyInformationStep = memo(
  ({ form }: PropertyInformationStepProps) => {
    const { setValidateOnChange } = useListingFormContext();

    // Disable aggressive validation when this step mounts
    useEffect(() => {
      setValidateOnChange(false);
      // setFormTouched(false); // No longer needed as formTouched is derived

      // Cleanup function to handle component unmount
      return () => {
        // Don't immediately validate when leaving
        setValidateOnChange(false);
      };
    }, [setValidateOnChange]); // Removed setFormTouched from dependencies

    return (
      <div className="space-y-6 property-info-form">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Home className="h-5 w-5 text-propertylk-blue" />
              Property Information
            </CardTitle>
            <CardDescription>
              Enter the basic details about your property
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormDescriptionMemo />
            <Suspense fallback={<SectionSkeleton />}>
              <BasicDetailsSection form={form} />
            </Suspense>
            <Suspense fallback={<SectionSkeleton />}>
              <PricingDetailsSection form={form} />
            </Suspense>
          </CardContent>
        </Card>
      </div>
    );
  }
);

PropertyInformationStep.displayName = "PropertyInformationStep";

export default PropertyInformationStep;
